<template>
  <v-container>
    <v-form ref="form" fast-fail @submit.prevent>
      <v-row>
        <v-col cols="auto">
          <v-icon
            size="large"
            :icon="CONSTANTS.ICONS_MDI.ICON_FILTER_OUTLINE"
            color="dark-cyan"
          ></v-icon>
        </v-col>
        <v-col align-self="center" class="relative-col">
          <h2 class="itemis-analyze-title"> {{ labels.GLOBAL_FILTER.NEW_FILTER }}</h2>
        </v-col>
        <v-col align-self="center" cols="auto">
          <v-btn
            id="back-btn"
            variant="flat"
            color="gray"
            prepend-icon="mdi-arrow-left"
            class="secondary-btn"
            @click="emit('closeEditMode')"
          >
            Back
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="3" sm="6" md="4">
          <analyze-name-description
            :input-name="componentProps.filter?.name ?? ''"
            :input-name-id="`name-field-global-filter-${componentProps?.filter?.id}`"
            :input-name-label="labels.GLOBAL_FILTER.GLOBAL_FILTERS"
            :input-name-placeholder="labels.GLOBAL_FILTER.DEFAULT_FILTER_NAME"
            :enable-description="false"
            :is-view-mode="false"
            @name-input="applyFilterName"
          />
        </v-col>
      </v-row>
      <div class="mt-16">
        <v-row v-for="(_, index) in expressions" :key="index" class="justify-center pt-0">
          <!-- expressions[index] is passed to v-model as it is a reference, otherwise sending just an expression
          (which can be specified in v-for="(expression, index) in ...")
          will be passed by value and will not update the actuall expressions array -->
          <v-col>
            <global-filter-constraint
              v-model:expression="expressions[index]"
              :disabled="false"
              @add-new-constraint="focusBtn"
            />
          </v-col>
          <v-col align-self="center" cols="auto">
            <v-btn
              v-if="expressions.length > 1"
              variant="outlined"
              size="small"
              icon="mdi-delete-outline"
              @click="deleteExpressionsInput(index)"
            />
          </v-col>
        </v-row>
        <v-row class="mb-7">
          <v-btn
            id="my-btn"
            class="ml-2"
            variant="flat"
            icon="mdi-plus"
            size="x-small"
            color="cyan-dark"
            @click="addExpressionsInput"
          />
        </v-row>
      </div>

      <v-spacer></v-spacer>
      <v-row>
        <v-col></v-col>
        <v-col cols="auto">
          <v-btn-group class="btn-group">
            <v-btn :min-width="130" class="secondary-btn" variant="flat" @click="cancel">
              {{ labels.GLOBAL_FILTER.CANCEL }}
            </v-btn>
          </v-btn-group>
        </v-col>

        <v-col cols="auto">
          <v-menu
            v-model="isMenuOpen"
            location="bottom"
            transition="fade-transition"
            :target="actionGroup"
          >
            <template #activator="{ props }">
              <v-btn-group ref="actionGroup" divided text class="btn-group">
                <v-btn
                  id="global-filter-save-btn"
                  min-width="130"
                  type="submit"
                  variant="flat"
                  class="primary-btn"
                  @click="saveFilter"
                >
                  {{ labels.GLOBAL_FILTER.SAVE }}
                </v-btn>
                <v-btn min-width="30" icon="mdi-menu-down" v-bind="props" class="toggle-btn">
                  <v-icon :class="isMenuOpen ? 'chevron-up' : 'chevron-down'"
                    >mdi-chevron-down</v-icon
                  >
                </v-btn>
              </v-btn-group>
            </template>
            <div class="mt-2">
              <v-btn
                ref="applyBtnRef"
                min-width="160"
                type="submit"
                variant="flat"
                class="primary-btn"
                @click="saveAndApplyFilter"
              >
                {{ labels.GLOBAL_FILTER.SAVE_AND_APPLY }}
              </v-btn>
            </div>
          </v-menu>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script setup lang="ts">
  import labels from '@/translations/labels.json'
  import { ref, watch } from 'vue'
  import { useGlobalFilterStore } from '@/stores/globalFilterStore'
  import { Expression, GlobalFilter, SingleExpression } from '@/models'
  import { useProjectStore } from '@/stores/projectStore'
  import GlobalFilterConstraint from '@/components/Inputs/GlobalFilterConstraint.vue'
  import CONSTANTS from '@/constants/Config.constants'
  import AnalyzeNameDescription from '@/components/AnalyzeNameDescription/AnalyzeNameDescription.vue'
  import { storeToRefs } from 'pinia'
  import { buildExpression, flattenExpression } from '@/utils/global-filters'
  import { VBtn, VForm } from 'vuetify/lib/components/index.mjs'
  import { Ref } from 'vue'

  interface Props {
    filter: GlobalFilter | undefined
  }

  const applyBtnRef = ref<VBtn>()
  const componentProps = defineProps<Props>()

  const emit = defineEmits<{
    (event: 'closeEditMode'): void
  }>()

  const globalFiltersStore = useGlobalFilterStore()
  const projectStore = useProjectStore()

  const { currentProject } = storeToRefs(projectStore)

  const filterName = ref<string>(componentProps.filter?.name ?? '')

  const expressions = ref<Array<SingleExpression | undefined>>([undefined])

  const form = ref<VForm | null>(null)
  const actionGroup = ref<Element | null>(null) as Ref<Element>
  const isMenuOpen = ref(false)

  watch(
    () => componentProps.filter?.expression,
    (value) => (expressions.value = flattenExpression(value)),
  )

  async function saveFilter(): Promise<GlobalFilter | undefined> {
    if (!currentProject?.value || !form.value) return

    const result = await form.value.validate()
    if (!result.valid) return

    const expression: Expression | null = buildExpression(
      expressions.value.filter((e): e is SingleExpression => e !== undefined),
    )

    let filter
    if (componentProps.filter) {
      const globalFilterToUpdate: GlobalFilter = {
        name: filterName.value,
        id: componentProps.filter.id,
        expression,
      }

      filter = await globalFiltersStore.updateGlobalFilter(
        currentProject.value.id,
        globalFilterToUpdate,
      )
    } else {
      const newGlobalFilter: Omit<GlobalFilter, 'id'> = {
        name: filterName.value,
        expression,
      }

      filter = await globalFiltersStore.createNewGlobalFilter(
        currentProject.value.id,
        newGlobalFilter,
      )
    }
    emit('closeEditMode')
    return filter
  }

  function applyFilterName(name: string) {
    filterName.value = name
  }

  async function saveAndApplyFilter() {
    const filter = await saveFilter()
    globalFiltersStore.applyGlobalFilter(filter)
  }
  function cancel(): void {
    // When closing the dialog for a new filter, remove any left-over constraints
    if (!componentProps.filter) {
      expressions.value = []
    }

    emit('closeEditMode')
  }

  async function addExpressionsInput(): Promise<void> {
    if (form.value && expressions.value.length > 0) {
      const result = await form.value.validate()

      if (!result.valid) return
    }

    expressions.value.push(undefined)
  }
  const focusBtn = () => {
    document.getElementById('my-btn')?.focus()
  }

  function deleteExpressionsInput(index: number) {
    if (expressions.value.length > 1) {
      expressions.value.splice(index, 1)
    }
  }
</script>

<style scoped>
  .inline-header-container {
    display: inline-flex;
    align-items: center;
  }

  .edit-icon {
    margin-left: 10px; /* Add space between the text and the icon */
  }

  .filter-name-input {
    max-width: 400px;
  }

  .primary-btn {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
  }

  .secondary-btn {
    background-color: rgb(var(--v-theme-itemis-hidden-grey));
    color: rgb(var(--v-theme-surface));
  }

  .btn-group {
    height: 2.5rem;
  }
  .toggle-btn {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
    margin: 0 !important;
    padding: 1px !important;
    width: auto;
  }

  .chevron-down {
    transform: rotate(0deg);
    transition: transform 0.2s ease-in-out;
  }
  .chevron-up {
    transform: rotate(180deg);
    transition: transform 0.2s ease-in-out;
  }
</style>

<!-- Not scoped because #create-filter-modal is created outside of this component! -->
<style>
  #create-filter-modal .v-card-title {
    min-width: 1200px;
    justify-content: center;
    display: flex;
  }
</style>
